import Dexie, { Table } from 'dexie'

import { DATASET_POLICIES, DatasetId } from './datasetPolicies'

import type { ILogger } from '@/infra/logger/ILogger'

/** Payload gravado: string para JSON, ArrayBuffer para os datasets binários. */
type DatasetPayload = string | ArrayBuffer

/**
 * Discriminador explícito do tipo de payload.
 *
 * Existe para ninguém precisar de `payload instanceof ArrayBuffer`: `instanceof` falha entre realms
 * diferentes de JS (jsdom é o caso óbvio, mas vale para qualquer contexto com mais de um realm), e a
 * falha seria silenciosa — o cache simplesmente nunca acertaria.
 */
export type DatasetPayloadKind = 'text' | 'binary'

interface DatasetRow {
  /** `<id>/v<schemaVersion>` — a versão faz parte da chave, então um bump é miss sem migração. */
  key: string
  fetchedAt: number
  sourceStamp: string | null
  itemCount: number | null
  kind: DatasetPayloadKind
  payload: DatasetPayload
}

/**
 * Banco DEDICADO, separado do `PlanningAppLocalDB`.
 *
 * Assim o cache de datasets (infra) não amarra sua evolução ao versionamento do banco da aplicação,
 * que já está na v10 e guarda dado do usuário — e um `.version()` novo aqui não arrisca nada lá.
 */
class DatasetCacheDB extends Dexie {
  datasets!: Table<DatasetRow, string>

  constructor() {
    super('PlanningAppDatasetCache')
    this.version(1).stores({ datasets: 'key' })
  }
}

export interface DatasetMeta {
  fetchedAt: number
  /** Carimbo para detectar mudança de conteúdo sem ETag (ver `readSourceStamp`). */
  sourceStamp: string | null
  itemCount: number | null
}

export type DatasetFreshness = 'fresh' | 'stale' | 'expired'

export interface DatasetCacheEntry {
  payload: DatasetPayload
  kind: DatasetPayloadKind
  meta: DatasetMeta
  freshness: DatasetFreshness
}

function cacheKeyFor(id: DatasetId): string {
  return `${id}/v${DATASET_POLICIES[id].schemaVersion}`
}

/**
 * Cache local dos datasets de referência, com TTL.
 *
 * **IndexedDB, e não Cache Storage.** A primeira versão usou Cache Storage, copiando o
 * `ObstacleRepository`, e isso estava errado: Cache Storage exige **contexto seguro**, e o dev roda em
 * `http://planning-dev.nexatlas.com:3000` — HTTP num hostname que não é `localhost`, logo
 * `isSecureContext === false` e `caches` fica indefinido. O cache seria código morto exatamente no
 * ambiente onde se desenvolve e se mede. (O Cache Storage do `ObstacleRepository` também nunca
 * funcionou em dev, o que passou despercebido porque ele degrada em silêncio.)
 *
 * O motivo que eu havia dado para preferir Cache Storage — evitar o structured-clone de 20.780
 * objetos — também não se aplica: o que é gravado é uma **string** serializada ou um `ArrayBuffer`, e
 * clonar isso é uma cópia de bytes, não uma travessia de objetos.
 *
 * Nenhuma falha de cache pode derrubar o boot: todos os métodos degradam para o comportamento
 * anterior (ir à rede) em vez de propagar erro.
 */
/**
 * Conexão única por processo.
 *
 * Deliberadamente em módulo, e não por instância: abrir várias conexões Dexie para o mesmo banco faz
 * uma bloquear a outra no `upgradeneeded`, e foi exatamente assim que a primeira versão disto falhou
 * no spec — duas instâncias de `DatasetCache` na mesma execução e a leitura voltava vazia.
 */
let sharedDB: DatasetCacheDB | null = null

export class DatasetCache {
  constructor(private readonly logger?: ILogger) {}

  isAvailable(): boolean {
    return typeof indexedDB !== 'undefined'
  }

  private getDB(): DatasetCacheDB | null {
    if (!this.isAvailable()) return null

    if (!sharedDB) {
      try {
        sharedDB = new DatasetCacheDB()
      } catch (error: any) {
        this.logger?.warn('DatasetCache', `Não foi possível abrir o banco: ${error?.message}`)
        return null
      }
    }

    return sharedDB
  }

  async read(id: DatasetId): Promise<DatasetCacheEntry | null> {
    const db = this.getDB()

    if (!db) return null

    try {
      const row = await db.datasets.get(cacheKeyFor(id))

      if (!row) return null

      if (!Number.isFinite(row.fetchedAt)) {
        await this.delete(id)
        return null
      }

      const policy = DATASET_POLICIES[id]

      // Escrita truncada (quota estourando no meio) produziria uma entrada plausível mas curta.
      if (row.itemCount !== null && row.itemCount < policy.minExpectedItems) {
        this.logger?.warn('DatasetCache', `${id}: entrada descartada (${row.itemCount} itens)`)
        await this.delete(id)
        return null
      }

      const meta: DatasetMeta = {
        fetchedAt: row.fetchedAt,
        sourceStamp: row.sourceStamp ?? null,
        itemCount: row.itemCount ?? null
      }

      return { payload: row.payload, kind: row.kind, meta, freshness: this.freshness(id, meta) }
    } catch (error: any) {
      this.logger?.warn('DatasetCache', `Falha lendo ${id}: ${error?.message}`)
      return null
    }
  }

  async write(id: DatasetId, payload: DatasetPayload, meta: Omit<DatasetMeta, 'fetchedAt'>): Promise<void> {
    const db = this.getDB()

    if (!db) return

    try {
      await db.datasets.put({
        key: cacheKeyFor(id),
        fetchedAt: Date.now(),
        sourceStamp: meta.sourceStamp,
        itemCount: meta.itemCount,
        kind: typeof payload === 'string' ? 'text' : 'binary',
        payload
      })
    } catch (error: any) {
      // Quota estourada, modo privado, contexto sem permissão: nada disso é motivo para falhar.
      this.logger?.warn('DatasetCache', `Falha gravando ${id}: ${error?.message}`)
    }
  }

  /** Renova só o carimbo de frescor, sem reescrever o payload. Para quando o conteúdo não mudou. */
  async touch(id: DatasetId): Promise<void> {
    const db = this.getDB()

    if (!db) return

    try {
      await db.datasets.update(cacheKeyFor(id), { fetchedAt: Date.now() })
    } catch (error: any) {
      this.logger?.warn('DatasetCache', `Falha renovando ${id}: ${error?.message}`)
    }
  }

  async delete(id: DatasetId): Promise<void> {
    const db = this.getDB()

    if (!db) return

    try {
      await db.datasets.delete(cacheKeyFor(id))
    } catch {
      // Sem recuperação possível nem necessária.
    }
  }

  freshness(id: DatasetId, meta: DatasetMeta): DatasetFreshness {
    const policy = DATASET_POLICIES[id]
    const ageMs = Date.now() - meta.fetchedAt

    if (ageMs <= policy.softTtlMs) return 'fresh'
    if (ageMs <= policy.hardTtlMs) return 'stale'

    return 'expired'
  }
}
